import { Component, Input, OnInit } from '@angular/core';

import { AuthService } from 'src/app/services/auth.service';
import { formatCurrency } from 'src/app/services/constants';
import { VoyageCreditAction } from 'src/app/services/operators/voyageradar-credit-actions';

/**
 * The Voyage Radar credit catalogue, as a list.
 *
 * One component for both surfaces that price credits — the purchase dialog and
 * the sensor preview's info modal — because they were about to render the same
 * rows from the same operator document (four of them then; six since port
 * calls) with two sets of icons and two spellings of "5 credits". The prices
 * themselves were never at risk of
 * drifting (both read `apiCreditActions` through `normalizeCreditActions`); the
 * PRESENTATION was, and a catalogue that describes an action differently
 * depending on which screen you reached it from is the same defect.
 *
 * Presentational only. It is handed a catalogue, never fetches one, and holds
 * no opinion about where the actions came from or whether they were filtered to
 * a target shape — that decision belongs to the caller, which is the only one
 * that knows whether a target exists yet.
 */
@Component({
  selector: 'app-voyage-credit-catalogue',
  templateUrl: './voyage-credit-catalogue.component.html',
  styleUrls: ['./voyage-credit-catalogue.component.scss'],
})
export class VoyageCreditCatalogueComponent implements OnInit {
  /** Already normalized and, where a target is known, already filtered. */
  @Input() actions: VoyageCreditAction[] = [];

  /** USD price of one credit. Ignored unless `showPricing` is set. */
  @Input() unitPrice = 0;

  /**
   * Money is only rendered when a price is actually known — the Outcome/Budget
   * path opens the purchase dialog with no unitcost at all, and the preview
   * hides currency from allocated users.
   */
  @Input() showPricing = false;

  /**
   * A wallet to measure the catalogue against. Set it and every row gains a
   * "buys N of these" column; leave it null and the same rows are a plain
   * price list. That difference is the whole reason the preview and the
   * purchase dialog can share this component.
   */
  @Input() credits: number | null = null;

  /**
   * Tag each row with the tasked shape it belongs to (AOI / POI).
   *
   * Off by default, because a caller that already filtered the catalogue to one
   * shape would be tagging the only option it shows. Only the unfiltered list
   * ever asks for it, and it asks of any catalogue that tags anything at all —
   * which the shipped one still does, on exactly one row. `nearby` dropped its
   * target because the entry prices an ENDPOINT rather than a gesture, and
   * /v1/vessels/nearby is shaped for a second caller — a search around a vessel
   * the user selects — which is build steps 4-8 of the nearby-vessel plan and
   * is not wired up yet; tagging the entry 'poi' would hide a row an area buyer
   * is meant to be able to spend on. So `refresh`/AOI is the one tagged entry
   * left, and the caller that sets this flag (`hasTargetVariants`, which asks
   * whether ANY entry carries a target) turns it on for that row alone. The two
   * refresh rows still sit adjacent at the same price; their descriptions are
   * now what tells them apart. Two tags appear only on a document that still
   * carries a per-shape tag on both: the LIVE operator document until the
   * migration re-syncs it, task snapshots taken before the rename, and ee-admin
   * edits.
   */
  @Input() showTargets = false;

  private activeUser: any;

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.authService.user.subscribe((user) => (this.activeUser = user));
  }

  /** Whether there is a wallet worth measuring rows against. */
  get showCounts(): boolean {
    return (this.credits ?? 0) > 0;
  }

  /**
   * One glyph per endpoint, keyed off the catalogue key — the shared vocabulary
   * with the backend. An ee-admin key we have no glyph for falls through to
   * `bolt` rather than to a wrong one.
   */
  iconFor(action: VoyageCreditAction): string {
    switch (action?.key) {
      case 'refresh':
        return 'radar';
      // Still `my_location` after the rename: the glyph reads as "around a
      // chosen centre", which is what this endpoint has always meant and will
      // go on meaning once the centre can be a vessel the user selects rather
      // than only the tasked point — that second caller is build steps 4-8 of
      // the nearby-vessel plan and is not built yet. It also has to stay
      // distinct from `refresh`'s radar, with which it shares a purchase-screen
      // price.
      case 'nearby':
        return 'my_location';
      case 'track':
        return 'timeline';
      case 'search':
        return 'travel_explore';
      case 'lookup':
        return 'directions_boat';
      default:
        return 'bolt';
    }
  }

  /**
   * The tasked shape this action belongs to, as the acronym both the tasking
   * screens and the operator's own documentation use. Empty for an entry with
   * no `target` — those apply to both shapes, and a tag reading "AOI / POI"
   * would be noise on every row that never needed telling apart.
   */
  targetLabel(action: VoyageCreditAction): string {
    switch (action?.target) {
      case 'aoi':
        return 'AOI';
      case 'poi':
        return 'POI';
      default:
        return '';
    }
  }

  /** How many of `action` the wallet buys. Zero when it buys none. */
  unitsAffordable(action: VoyageCreditAction): number {
    const wallet = this.credits ?? 0;
    return action?.credits > 0 ? Math.floor(wallet / action.credits) : 0;
  }

  actionCostLabel(action: VoyageCreditAction): string {
    return this.costLabel((action?.credits ?? 0) * this.unitPrice);
  }

  /** "1 credit", "5 credits" — the catalogue prints a 1 often enough to matter. */
  creditsLabel(n: number): string {
    return `${this.formatCredits(n)} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /** Thousands separators — a bare 5000 reads as a typo next to money. */
  formatCredits(n: number | null): string {
    return Number(n || 0).toLocaleString('en-US');
  }

  /**
   * Currency or points, honouring the user's conversion setting. Points-based
   * customers must never be shown dollars.
   */
  costLabel(cost: number): string {
    if (this.activeUser?.pointsEligible && this.activeUser?.sensorConversionFactor) {
      const [part1, part2] = this.activeUser.sensorConversionFactor.split(':').map(parseFloat);
      const conversionFactor = part2 / part1;
      return `${Math.round(cost * conversionFactor)} Points`;
    }
    return formatCurrency(cost);
  }
}
